import { LeftOutlined } from "@ant-design/icons";
import { Checkbox, Col, Flex, Row } from "antd";
import RadioButton from "components/RadioButton";
import Button from "components/button/Button";
import CKEditorCustom from "components/ckeditor";
import DatePickerComponent from "components/datepicker/Datepicker";
import InputComponent from "components/formInput/Input";
import Loading from "components/loading/Loading";
import PopupConfirm from "components/popup-confirm";
import { useFormik } from "formik";
import {
  apiCreateHelp,
  apiDeleteHelp,
  apiGetHelp,
  apiUpdateHelp,
} from "helpers/api/faqs";
import { keys, omit, some } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { showToastError, showToastSuccess } from "utils";
import * as yup from "yup";
import "./styles.scss";

const HelpForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [loading, setLoading] = useState(0);
  const isEdit = Boolean(id);

  const {
    values,
    errors,
    handleChange,
    setFieldValue,
    resetForm,
    validateForm,
  } = useFormik({
    validateOnChange: false,
    initialValues: {
      subject: "",
      content: "",
      content_no_html: "",
      public: true,
      mobile: undefined,
    },
    validationSchema: yup.object().shape({
      subject: yup
        .string()
        .max(50, t("messages.MSG-USP052", { number: 50 }))
        .required(),
      content: yup.string(),
      content_no_html: yup
        .string()
        .max(5000, t("messages.MSG-USP052", { number: 5000 })),
      public: yup.boolean().required(),
      date: yup.date(),
      time: yup.date(),
      mobile: yup.boolean().required(),
    }),
  });
  const isFuture = moment(values.date).isAfter(moment(), "day");

  const getHelp = () => {
    setLoading((prev) => prev + 1);
    apiGetHelp(id)
      .then(({ error, data }) => {
        if (error) {
          showToastError(error.message);
        }
        if (data) {
          resetForm({
            values: {
              ...data,
              date: data.release_date
                ? moment.utc(data.release_date).toDate()
                : "",
              time: data.release_date
                ? moment.utc(data.release_date).toDate()
                : "",
            },
          });
        }
      })
      .catch(({ error }) => {
        showToastError(`messages.${error.reason}`);
      })
      .finally(() => {
        setLoading((prev) => prev - 1);
      });
  };

  const createHelp = () => {
    setLoading((prev) => prev + 1);
    apiCreateHelp({
      ...omit(values, ["date", "time"]),
      release_date: moment(
        `${moment(values.date).format("YYYY-MM-DD")} ${moment(
          values.time
        ).format("HH:mm:ss")}`
      )
        .utc()
        .format(),
    })
      .then((res) => {
        if (res.error) {
          showToastError(res.error.message);
        } else {
          history.push("/admin/help-faqs");
          showToastSuccess("messages.MSG-USP092");
        }
      })
      .catch(({ error }) => {
        showToastError(`messages.${error.reason}`);
      })
      .finally(() => {
        setLoading((prev) => prev - 1);
      });
  };

  const updateHelp = () => {
    setLoading((prev) => prev + 1);
    apiUpdateHelp({
      ...omit(values, ["date", "time"]),
      release_date: moment(
        `${moment(values.date).format("YYYY-MM-DD")} ${moment(
          values.time
        ).format("HH:mm:ss")}`
      )
        .utc()
        .format(),
    })
      .then((res) => {
        if (res.error) {
          showToastError(res.error.message);
        } else {
          history.push("/admin/help-faqs");
          showToastSuccess("messages.MSG-USP055");
        }
      })
      .catch(({ error }) => {
        showToastError(`messages.${error.reason}`);
      })
      .finally(() => {
        setLoading((prev) => prev - 1);
      });
  };

  const deleteHelp = () => {
    setLoading((prev) => prev + 1);
    apiDeleteHelp(values.id)
      .then((res) => {
        if (res.error) {
          showToastError(res.error.message);
        } else {
          history.push("/admin/help-faqs");
          showToastSuccess("messages.MSG-USP040");
        }
      })
      .catch(({ error }) => {
        showToastError(`messages.${error.reason}`);
      })
      .finally(() => {
        setLoading((prev) => prev - 1);
      });
  };

  const handleHelp = async () => {
    const isValid = Boolean(!keys(await validateForm()).length);
    if (isValid) {
      if (!isEdit) {
        createHelp();
      } else {
        updateHelp();
      }
    }
  };

  useEffect(() => {
    if (id) {
      getHelp();
    }
  }, [id]);

  useEffect(() => {
    setFieldValue(
      "content_no_html",
      document.getElementById(`ckeditor-${id}`)?.innerText
    );
  }, [values.content]);

  return (
    <div className="help-create-container main-content">
      <div className="root-right-header">
        <div className="screen-name">{t("admin_role")}</div>
      </div>
      <div className="nav">
        <Flex gap="large" wrap="wrap" align="center">
          <div
            className="screen-title"
            onClick={() => history.push("/admin/help-faqs")}
          >
            <LeftOutlined />
            {t(`helps.form.${!isEdit ? "createTitle" : "editTitle"}`)}
          </div>
        </Flex>

        <Row className="help-form" gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <div className="help-input">
              <div className="input-title">{t("helps.form.title")}</div>
              <InputComponent
                style={{ border: "none" }}
                placeholder={t("helps.form.title")}
                name="subject"
                value={values?.subject}
                error={errors?.subject}
                onChange={handleChange}
              />
            </div>
            <div style={{ marginTop: "10px", display: "flex", flexDirection: "column" }}>
              <div className="input-title">{t("helps.form.content")}</div>
              <CKEditorCustom
                showToolbar={false}
                name="content"
                placeholder={t("helps.form.content")}
                value={values?.content}
                error={errors?.content_no_html}
                onChange={(_, editor) => {
                  setFieldValue("content", editor.getData());
                  editor.ui.getEditableElement().id = `ckeditor-${id}`;
                }}
              />
            </div>
          </Col>

          <Col xs={24} md={8} >
            <div style={{ height: "512px" }}>
              <div className="public-session">
                <RadioButton
                  title={t("helps.form.publicPrivate")}
                  options={[
                    { title: t("helps.form.public"), value: true },
                    { title: t("helps.form.private"), value: false },
                  ]}
                  name="public"
                  value={values?.public}
                  onChange={handleChange}
                />
                <div className="time-section">
                  <div className="time-section-title">{t(`helps.form.publicDate`)}</div>
                  <DatePickerComponent
                    prefix="calendar-gray.png"
                    placeholder={"YYYY/MM/DD"}
                    format="YYYY/MM/DD"
                    name="date"
                    value={values?.date}
                    error={errors?.date}
                    onChange={(e) => setFieldValue("date", e)}
                  />
                  <DatePickerComponent
                    prefix="clock-gray.png"
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={1}
                    placeholder={"HH:MM"}
                    dateFormat="HH:mm"
                    name="time"
                    value={values?.time}
                    error={errors?.time}
                    onChange={(e) => setFieldValue("time", e)}
                  />
                </div>
              </div>

              <div className="public-session">
                <div>
                  <div style={{ fontWeight: "bold" }}>
                    {t("helps.form.destination")}
                  </div>
                  <Flex vertical>
                    <Checkbox
                      name="mobile"
                      checked={values?.mobile === false}
                      onChange={(checked) => {
                        if (checked) setFieldValue("mobile", false);
                      }}
                    >
                      {t("helps.form.clinicViewer")}
                    </Checkbox>
                    <Checkbox
                      name="mobile"
                      checked={values?.mobile === true}
                      title={t("helps.form.app")}
                      onChange={(checked) => {
                        if (checked) setFieldValue("mobile", true);
                      }}
                    >
                      {t("helps.form.app")}
                    </Checkbox>
                  </Flex>
                </div>
              </div>
            </div>
          </Col>
        </Row >

        <Row gutter={[16, 16]} className="help-button" style={{ marginTop: 24 }}>
          <Col xs={24} md={8}>
            <Button
              text={t("helps.form.save")}
              width="100%"
              height={40}
              type="button"
              className="save-button action-btn"
              style={{
                fontSize: 20,
                fontWeight: "bold",
                borderRadius: 60,
                margin: 0,
              }}
              onClick={() => handleHelp()}
              disabled={some(
                Object.values(omit(values, ["date", "time"])),
                (item) => !item && item !== false
              )}
            />
          </Col>

          <Col xs={24} md={12}>
            <Button
              text={t("helps.form.deleteArticle")}
              width="100%"
              height={40}
              background="#FF3131"
              color="#FFFFFF"
              type="button"
              disabled={!isEdit}
              onClick={() => setIsOpenDelete(true)}
              style={{
                fontSize: 20,
                fontWeight: "bold",
                borderRadius: 60,
              }}
              className={'action-btn'}
            />
          </Col>
        </Row>
      </div >

      <PopupConfirm
        open={isOpenDelete}
        onCancelText={t("button.cancel")}
        onOkText={t("button.delete")}
        content={t("confirm_delete")}
        onOk={() => deleteHelp()}
        onCancel={() => setIsOpenDelete(false)}
        onOkColor="#FF3131"
      />

      {loading ? <Loading /> : null}
    </div >
  );
};

export default HelpForm;
